import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, Smartphone, Building2, ShieldCheck, Upload, Image as ImageIcon, Sparkles, AlertCircle, Lock, Download, QrCode } from "lucide-react";
import api, { fmtINR, formatDetail, BACKEND_URL } from "@/lib/api";
import { downloadQr, probeRedirectUrl } from "@/lib/qrUtils";
import AntiScreenshotQR from "@/components/AntiScreenshotQR";
import ManualUpiPanel from "@/components/payment/ManualUpiPanel";
import UpiIntentButtons from "@/components/payment/UpiIntentButtons";
import FieldCopy from "@/components/payment/FieldCopy";
import ConfettiBurst from "@/components/payment/ConfettiBurst";
import PaymentResultScreen from "@/components/payment/PaymentResultScreen";
import QrZoomModal from "@/components/payment/QrZoomModal";


export default function PaymentPage() {
    const { txnId } = useParams();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("upi");
    const [utr, setUtr] = useState("");
    const [amountPaid, setAmountPaid] = useState("");
    const [screenshot, setScreenshot] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [ocrResult, setOcrResult] = useState(null); // {kind: 'success'|'soft_fail', utr_filled, amount_filled, app, reason}
    const [autoFilled, setAutoFilled] = useState({ utr: false, amount: false });
    const [err, setErr] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [copied, setCopied] = useState("");
    const [remaining, setRemaining] = useState(null);
    const [statusInfo, setStatusInfo] = useState(null);
    const [redirectIn, setRedirectIn] = useState(null);
    // Set to true when our pre-flight probe of the merchant's
    // success_redirect / failure_redirect URL fails (DNS error, blocked
    // by CORS preflight at the network layer, connection refused, etc).
    // When true we DO NOT auto-redirect — the customer would land on a
    // Chrome NXDOMAIN error page and lose all confirmation that their
    // payment went through. Instead we keep them on our success card and
    // show a friendly explanatory note. They can still tap "Continue" to
    // try anyway.
    const [redirectBroken, setRedirectBroken] = useState(false);
    // QR is hidden by default in the classic intent flow (intent buttons
    // get >95% of taps; the QR mostly takes vertical space). Customer
    // can toggle it on with the "Show QR" link below the intent row.
    const [qrVisible, setQrVisible] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const r = await api.get(`/public/payment-page/${txnId}`);
        setData(r.data);
        setTab(r.data.method === "bank" ? "bank" : "upi");
        if (r.data.utr) { setSubmitted(true); setUtr(r.data.utr); }
    };
    useEffect(() => { load();   }, [txnId]);

    // After UTR is submitted, poll for approval status. Stop once terminal.
    useEffect(() => {
        const isWaiting = submitted || data?.status === "submitted";
        if (!isWaiting) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const r = await api.get(`/public/payment-page/${txnId}/status`);
                if (cancelled) return;
                setStatusInfo(r.data);
                if (r.data.status === "success" || r.data.status === "failed") {
                    // Broadcast success up to a parent iframe / opener. The
                    // embed widget listens for this and auto-closes the
                    // modal (per merchant `auto_close_on_success` setting).
                    // Broadcasting on BOTH status types is intentional —
                    // failure should also dismiss so the customer isn't
                    // stuck staring at a failed card inside a modal.
                    try {
                        if (window.parent && window.parent !== window) {
                            window.parent.postMessage({
                                type: r.data.status === "success" ? "paygate:success" : "paygate:failed",
                                txn_id: txnId,
                                utr: r.data.utr,
                            }, "*");
                        }
                        if (window.opener) {
                            window.opener.postMessage({
                                type: r.data.status === "success" ? "paygate:success" : "paygate:failed",
                                txn_id: txnId,
                                utr: r.data.utr,
                            }, "*");
                        }
                    } catch { /* postMessage is best-effort */ }
                    if (r.data.redirect_url) {
                        // Bump countdown to 5s (was 3s) — short enough to
                        // feel snappy, long enough that the customer actually
                        // perceives the success card before being whisked
                        // away. Critical when the redirect is broken: at
                        // least they SAW the success.
                        setRedirectIn(5);
                        // Fire a pre-flight reachability probe in parallel.
                        // We use a no-cors HEAD with a 2.5s timeout. The
                        // response is "opaque" (we can't inspect it), but
                        // any DNS / connection-refused / network-down error
                        // makes the promise REJECT, which is exactly the
                        // signal we want — "this URL is going to send the
                        // customer to a Chrome error page; don't redirect".
                        probeRedirectUrl(r.data.redirect_url).then((ok) => {
                            if (!ok) setRedirectBroken(true);
                        });
                    }
                    return true;
                }
            } catch { /* keep polling */ }
            return false;
        };
        let stopped = false;
        // Exponential-backoff schedule for the status poll. Most txns settle
        // in under 30s (smart-OCR auto-approve) — those need a tight 4s poll
        // to feel instant. The long tail (manual approvals, stale UTRs) can
        // sit for 10+ minutes; polling every 4s on those is ~150 redundant
        // hits/customer for nothing.
        //   tick 1:    4s   (snap responsiveness on the fast path)
        //   tick 2:    4s
        //   tick 3:    8s
        //   tick 4:    8s
        //   tick 5+:  15s
        const BACKOFF = [4000, 4000, 8000, 8000, 15000];
        let attempt = 0;
        let timer = null;
        const schedule = () => {
            if (stopped) return;
            const wait = BACKOFF[Math.min(attempt, BACKOFF.length - 1)];
            timer = setTimeout(async () => {
                if (stopped) return;
                attempt += 1;
                const done = await tick();
                if (done) { stopped = true; return; }
                schedule();
            }, wait);
        };
        tick();
        schedule();
        return () => { cancelled = true; stopped = true; if (timer) clearTimeout(timer); };
    }, [submitted, data?.status, txnId]);

    // Redirect countdown after terminal state with a redirect URL.
    // CRITICAL: we DO NOT auto-redirect if `redirectBroken` is true — the
    // customer would be sent to a Chrome NXDOMAIN error and lose all
    // confirmation that their payment went through. They can still tap
    // the "Continue anyway" button if they want to try.
    useEffect(() => {
        if (redirectIn == null) return;
        if (redirectBroken) return;
        if (redirectIn <= 0 && statusInfo?.redirect_url) {
            window.location.href = statusInfo.redirect_url;
            return;
        }
        const t = setTimeout(() => setRedirectIn((s) => (s == null ? null : s - 1)), 1000);
        return () => clearTimeout(t);
    }, [redirectIn, statusInfo, redirectBroken]);

    useEffect(() => {
        if (!data) return;
        const exp = new Date(data.expires_at);
        const tick = () => {
            const ms = exp - new Date();
            setRemaining(Math.max(0, Math.floor(ms / 1000)));
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [data]);

    const copy = useCallback((k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); }, []);

    // Anti-screenshot deterrents (mobile-first):
    //   1. Block right-click (desktop) / long-press save image (mobile via CSS).
    //   2. Intercept Print Screen on desktop and clear the clipboard.
    //   3. Blur the QR when the tab/page becomes hidden (catches the brief
    //      "page hidden" moment iOS triggers when the screenshot flash fires;
    //      Android Quick Settings / iOS Control Center also fire this). Once
    //      hidden has been observed, we keep the shield up for 4s after the
    //      page returns to visible — long enough that the screenshot already
    //      taken captures the obscured state, not the QR.
    //   4. Browsers cannot block OS-level screenshots. The forensic watermark
    //      below the QR ensures any leaked screenshot is traceable to this txn.
    const [screenshotShield, setScreenshotShield] = useState(false);
    // --- Anti-decline flow (Fix #2 + #3) ----------------------------------
    // Tracks the timestamp of the customer's most recent intent-button tap.
    // When the page becomes visible again within 30s of that tap and they
    // haven't submitted a UTR, we treat it as a likely intent decline
    // (Yes Bank / NPCI risk policy etc). First decline → in-page nudge;
    // second decline AND merchant has `auto_rotate_on_fail` ON → server-
    // rotate to a fresh channel and refresh the snapshot/QR/intent inline.
    const [intentTapAt, setIntentTapAt] = useState(0);
    const [failCount, setFailCount] = useState(0);
    const [rotateBusy, setRotateBusy] = useState(false);
    const [rotateToast, setRotateToast] = useState("");
    const onIntentTap = useCallback(() => { setIntentTapAt(Date.now()); }, []);
    const tryRotate = async () => {
        if (rotateBusy || !data?.auto_rotate_on_fail) return;
        setRotateBusy(true);
        try {
            const r = await fetch(`${BACKEND_URL}/api/public/payment-page/${txnId}/rotate-channel`, {
                method: "POST", headers: { "Content-Type": "application/json" },
            });
            const j = await r.json();
            if (j.ok && j.upi_intent) {
                setData((d) => ({ ...d, snapshot: j.snapshot, upi_intent: j.upi_intent, qr_image: j.qr_image }));
                setFailCount(0);
                setIntentTapAt(0);
                setRotateToast("Switched to a fresh channel — please try again.");
            } else if (j.reason === "no_alt_channel") {
                // No more UPI channels — try bank fallback if enabled.
                if (data?.fallback_to_bank) {
                    await trySwitchToBank();
                    return;
                }
                setRotateToast("No alternate channel available right now. Please scan the QR with a different UPI app or use Bank Transfer.");
            } else if (j.reason === "rotation_disabled") {
                setRotateToast("Please scan the QR with a different UPI app or use Bank Transfer.");
            } else {
                setRotateToast("Couldn't rotate. Try scanning the QR with another UPI app.");
            }
            setTimeout(() => setRotateToast(""), 4500);
        } catch {
            setRotateToast("Network hiccup — please try again.");
            setTimeout(() => setRotateToast(""), 3000);
        } finally {
            setRotateBusy(false);
        }
    };

    // Switch the customer from UPI to Bank-transfer flow when the merchant
    // has fallback_to_bank enabled and UPI rotations have exhausted (or the
    // merchant has fallback enabled without rotation). Backend assigns an
    // eligible bank channel and updates the txn snapshot; we just refetch
    // /payment-page to render the bank-account view.
    const trySwitchToBank = async () => {
        try {
            const r = await fetch(`${BACKEND_URL}/api/public/payment-page/${txnId}/switch-to-bank`, {
                method: "POST", headers: { "Content-Type": "application/json" },
            });
            const j = await r.json();
            if (j.ok) {
                // Refresh the whole payment-page so we get the canonical
                // snapshot (and any per-link bank fields the server adds).
                const fresh = await fetch(`${BACKEND_URL}/api/public/payment-page/${txnId}`);
                if (fresh.ok) {
                    const newData = await fresh.json();
                    setData(newData);
                }
                setFailCount(0);
                setIntentTapAt(0);
                setRotateToast("Switched to Bank Transfer. Please copy the account details below and complete the transfer.");
                setTimeout(() => setRotateToast(""), 6000);
            } else if (j.reason === "no_bank_channel") {
                setRotateToast("No bank channel available right now. Please retry in a few minutes or contact support.");
                setTimeout(() => setRotateToast(""), 5000);
            } else {
                setRotateToast("Couldn't switch to Bank Transfer. Please try a different UPI app.");
                setTimeout(() => setRotateToast(""), 4000);
            }
        } catch {
            setRotateToast("Network hiccup — please try again.");
            setTimeout(() => setRotateToast(""), 3000);
        }
    };
    // Whenever the customer comes back to the page after a recent intent
    // tap without a UTR, count it as a likely failure attempt. We use
    // the screenshot-shield's visibilitychange listener as the trigger,
    // but isolate the failure logic with its own effect so the two
    // concerns stay independent.
    useEffect(() => {
        if (!data || data.utr) return;
        const handler = () => {
            if (document.visibilityState !== "visible") return;
            if (!intentTapAt) return;
            const dt = Date.now() - intentTapAt;
            if (dt < 1500 || dt > 30000) return; // outside the failure window
            // Reset tap stamp so this only fires once per intent attempt.
            setIntentTapAt(0);
            setFailCount((c) => {
                const next = c + 1;
                if (next === 1) {
                    setRotateToast("If your UPI app declined, try scanning the QR with another app or rotate channel.");
                    setTimeout(() => setRotateToast(""), 4500);
                } else if (next >= 2 && data.auto_rotate_on_fail) {
                    // Auto-rotate after the 2nd consecutive failure. If
                    // rotate exhausts UPI alternatives, tryRotate() will
                    // chain into trySwitchToBank() when fallback is on.
                    tryRotate();
                } else if (next >= 2 && data.fallback_to_bank) {
                    // Fallback-only path: no rotation, but bank is allowed.
                    trySwitchToBank();
                } else if (next >= 2) {
                    setRotateToast("Looks like the UPI app declined again. Please scan the QR with another app or use Bank Transfer.");
                    setTimeout(() => setRotateToast(""), 4500);
                }
                return next;
            });
        };
        document.addEventListener("visibilitychange", handler);
        return () => document.removeEventListener("visibilitychange", handler);
         
    }, [data, intentTapAt]);
    // Hold-to-view mode is now controlled by the merchant per payment-link
    // (data.qr_hold_to_reveal). The customer can no longer toggle it — they
    // just see the cover if the merchant enabled it on this link.
    const qrHoldMode = !!data?.qr_hold_to_reveal;
    const [qrRevealing, setQrRevealing] = useState(false);
    // Click-to-zoom: when the customer taps the QR thumbnail, open a modal
    // rendering the same temporal-multiplexed canvas at large size for easy
    // scanning. Modal inherits all anti-screenshot deterrents.
    const [qrZoom, setQrZoom] = useState(false);
    // Track in-flight share/download so we can disable buttons & show a tiny
    // spinner instead of letting the customer fire the canvas pipeline twice.
    const [qrActionBusy, setQrActionBusy] = useState(null); // null | "download" | "share"
    // Toast shown after a successful share/download from inside the zoom modal.
    const [qrShareNote, setQrShareNote] = useState("");
    useEffect(() => {
        if (!qrShareNote) return;
        const t = setTimeout(() => setQrShareNote(""), 2500);
        return () => clearTimeout(t);
    }, [qrShareNote]);
    // Reset reveal when leaving hold-mode (rare: link config changes mid-session)
    // or when zoom modal closes (so re-opening starts covered).
    useEffect(() => { if (!qrHoldMode) setQrRevealing(false); }, [qrHoldMode]);
    useEffect(() => { if (!qrZoom && qrHoldMode) setQrRevealing(false); }, [qrZoom, qrHoldMode]);
    // ESC closes zoom modal.
    useEffect(() => {
        if (!qrZoom) return;
        const onEsc = (e) => { if (e.key === "Escape") setQrZoom(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [qrZoom]);
    // Auto-cover when the tab is hidden (defence-in-depth — also exits zoom).
    useEffect(() => {
        if (!qrHoldMode || !qrRevealing) return;
        const onHide = () => { if (document.visibilityState === "hidden") setQrRevealing(false); };
        document.addEventListener("visibilitychange", onHide);
        return () => document.removeEventListener("visibilitychange", onHide);
    }, [qrHoldMode, qrRevealing]);
    useEffect(() => {
        const onCtx = (e) => { e.preventDefault(); return false; };
        const onKey = (e) => {
            if (e.key === "PrintScreen" || (e.ctrlKey && e.key === "p")) {
                try { navigator.clipboard.writeText(""); } catch { /* ignore */ }
                setScreenshotShield(true);
                setTimeout(() => setScreenshotShield(false), 4000);
            }
        };
        let shieldTimer;
        const onVisChange = () => {
            if (document.visibilityState === "hidden") {
                setScreenshotShield(true);
                clearTimeout(shieldTimer);
                // Keep blurred for 4s after page returns visible — this is
                // when iOS actually saves the screenshot.
                shieldTimer = setTimeout(() => setScreenshotShield(false), 4000);
            }
        };
        document.addEventListener("contextmenu", onCtx);
        document.addEventListener("keyup", onKey);
        document.addEventListener("visibilitychange", onVisChange);
        return () => {
            document.removeEventListener("contextmenu", onCtx);
            document.removeEventListener("keyup", onKey);
            document.removeEventListener("visibilitychange", onVisChange);
            clearTimeout(shieldTimer);
        };
    }, []);

    const onPickFile = async (file) => {
        if (!file) return;
        if (file.size > 6 * 1024 * 1024) { setErr("File too large (max 6MB)."); return; }
        const reader = new FileReader();
        reader.onload = () => setScreenshot(reader.result);
        reader.readAsDataURL(file);
        // ─── Hybrid OCR ────────────────────────────────────────────
        // Pass 1 (FREE): Tesseract in the customer's browser. Catches
        // ~85% of clean PhonePe/GPay screenshots in 1–2 seconds with
        // ZERO LLM credit burn. Lazy-loaded so the page bundle stays
        // light for customers who never upload.
        // Pass 2 (PAID): Server-side Gemini Vision via /api/public/ocr.
        // Only fires when Tesseract returns no usable UTR (blurry,
        // cropped, dark mode with poor contrast, rotated, etc.).
        setOcrBusy(true); setErr(""); setOcrResult(null);
        let filledFromClient = false;
        try {
            const { extractUtrClientSide } = await import("@/lib/clientOcr");
            const clientRes = await extractUtrClientSide(file);
            if (clientRes.utr) {
                // Tesseract nailed it — no server round-trip needed.
                setUtr(clientRes.utr);
                setAutoFilled({ utr: true, amount: false });
                setOcrResult({
                    kind: "success",
                    utr_filled: true,
                    amount_filled: false,
                    app: "Other",
                    provenance: "client",
                });
                filledFromClient = true;
            }
        } catch (clientErr) {
            console.warn("client-side OCR unavailable:", clientErr);
        }
        if (filledFromClient) { setOcrBusy(false); return; }

        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`${BACKEND_URL}/api/public/ocr`, { method: "POST", body: fd });
            if (!res.ok) throw new Error("OCR failed");
            const j = await res.json();
            const filledUtr = !!j.utr;
            if (filledUtr) {
                setUtr(j.utr);
                setAutoFilled({ utr: true, amount: false });
                setOcrResult({
                    kind: "success",
                    utr_filled: true,
                    amount_filled: false,
                    app: j.payment_app || "Other",
                    provenance: "server",
                });
            } else {
                setOcrResult({
                    kind: "soft_fail",
                    reason: j.reason || "Couldn't read UTR from this image. You can still submit — we'll verify manually.",
                });
            }
        } catch {
            setOcrResult({
                kind: "soft_fail",
                reason: "Couldn't process the image right now. You can still submit — we'll verify manually.",
            });
        } finally {
            setOcrBusy(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            // Server-side validation requires at least one of utr|screenshot;
            // the form is gated on the same rule below (submit button
            // disabled when both are missing). When the UTR is blank we
            // send null — the backend stores `utr=null` and flips
            // `awaiting_manual_utr=true` so the Approvals UI can surface
            // a "screenshot only" badge.
            const cleanUtr = (utr || "").trim();
            await api.post(`/public/payment-page/${txnId}/utr`, {
                utr: cleanUtr.length >= 6 ? cleanUtr : null,
                amount_paid: amountPaid ? parseFloat(amountPaid) : null,
                screenshot_b64: screenshot,
            });
            setSubmitted(true); load();
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    if (!data) return <div className="public-bg min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

    const snap = data.snapshot || {};
    const minutes = Math.floor((remaining || 0) / 60);
    const seconds = (remaining || 0) % 60;
    const expired = data.expired || (remaining === 0 && !submitted);

    if (submitted || data.status === "submitted" || data.status === "success") {
        return (
            <PaymentResultScreen
                data={data}
                statusInfo={statusInfo}
                utr={utr}
                redirectIn={redirectIn}
                redirectBroken={redirectBroken}
            />
        );
    }

    return (
        <div className="public-bg min-h-screen flex flex-col">
            <div className="px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">P</div>
                    <span className="font-bold tracking-tight">PayGateway</span>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Secure</div>
            </div>

            <div className="flex-1 flex items-start sm:items-center justify-center px-3 sm:px-4 pb-6">
                <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-12 lg:gap-5 lg:items-start">

                    {/* ── Left panel: merchant + amount + timer (desktop ≥lg) */}
                    <aside className="hidden lg:flex lg:col-span-3 lg:sticky lg:top-6 flex-col items-center text-center card p-5 self-start">
                        {data.partner_logo && (
                            <img src={data.partner_logo} alt={data.partner_name}
                                 className="mb-3 w-20 h-20 rounded-xl object-contain bg-white shadow-sm ring-1 ring-slate-200 p-1.5" />
                        )}
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Pay to</div>
                        <div className="font-bold text-base mt-0.5">{data.partner_name}</div>
                        <div className="border-t border-slate-100 w-full my-4" />
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                        <div className="font-mono text-3xl font-bold text-slate-900 mt-0.5">{fmtINR(data.amount)}</div>
                        <div className="text-[11px] text-slate-500 mt-2">For</div>
                        <div className="text-sm font-medium">{data.username}</div>
                        {!expired && remaining != null && (
                            <div className={`mt-4 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${remaining < 60 ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                                ⏳ Expires in {minutes}:{String(seconds).padStart(2, "0")}
                                {data.session_extended && (
                                    <span data-testid="session-extended-badge"
                                          className="ml-1 text-[9px] uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                        Extended
                                    </span>
                                )}
                            </div>
                        )}
                        {expired && <div className="mt-3 text-xs text-amber-700">Session timer expired — you can still submit UTR if paid.</div>}
                        <div className="mt-5 pt-4 border-t border-slate-100 w-full text-left text-[11px] text-slate-500 space-y-1.5">
                            <div className="font-semibold text-slate-600 mb-1">Transaction</div>
                            <div className="font-mono text-[10px] break-all">{data.txn_id}</div>
                        </div>
                    </aside>

                    {/* ── Center panel: action card (QR/bank/UPI tap) */}
                    <main className="lg:col-span-6 w-full max-w-md mx-auto lg:max-w-none">

                    {/* Mobile/tablet header — hidden on desktop where left panel takes over */}
                    <div className="text-center mb-2.5 sm:mb-4 lg:hidden">
                        <div className="flex items-center justify-center gap-2">
                            {data.partner_logo && (
                                <img src={data.partner_logo} alt={data.partner_name}
                                     className="w-9 h-9 rounded-lg object-contain bg-white shadow-sm ring-1 ring-slate-200 p-0.5" />
                            )}
                            <div className="text-left">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-none">Pay to {data.partner_name}</div>
                                <div className="font-mono text-xl font-bold leading-tight">{fmtINR(data.amount)}</div>
                            </div>
                            {!expired && remaining != null && (
                                <div className={`ml-auto inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${remaining < 60 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                                    ⏳ {minutes}:{String(seconds).padStart(2, "0")}
                                </div>
                            )}
                        </div>
                        {expired && <div className="mt-1 text-[11px] text-amber-700">Session timer expired — you can still submit UTR if paid.</div>}
                    </div>

                    {/* (Mobile tabs removed per user request — single-page, no scroll on mobile.) */}

                    <div className="card overflow-hidden">
                        {data.method === "upi" && (snap.upi_id || data.qr_image) && (
                            <div className="p-2 sm:p-5 pay-protected">
                                <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 sm:mb-2.5 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Pay via UPI</div>
                                {/* QR is rendered inline ONLY when:
                                    - manual UPI mode (customer relies on QR/copy since
                                      no intent buttons), OR
                                    - the customer explicitly toggled "Show QR" in the
                                      classic intent flow (qrVisible). Hidden by default
                                      because intent buttons get >95% of taps and the QR
                                      was eating large amounts of vertical space. */}
                                {data.qr_image && (data.manual_upi_mode || qrVisible) && (
                                    <div className="text-center mb-1.5 sm:mb-2">
                                        <button
                                            type="button"
                                            data-testid="qr-zoom-trigger"
                                            onClick={() => { if (!qrHoldMode) setQrZoom(true); }}
                                            disabled={qrHoldMode}
                                            aria-label="Tap to zoom QR"
                                            className={`inline-block bg-white p-1 sm:p-2 rounded-lg ring-2 ring-blue-100 pay-watermark relative ${qrHoldMode ? "" : "cursor-zoom-in hover:ring-blue-300 transition-all"}`}
                                            data-watermark={`${data.txn_id} · ${data.username}\n${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}`}
                                            onContextMenu={(e) => e.preventDefault()}
                                        >
                                            {/* QR is rendered through AntiScreenshotQR — temporal-multiplexed
                                                across two complementary noise-corrupted frames at 60fps so any
                                                single screenshot or screen-photo captures only half the QR
                                                modules (with the other half replaced by random noise),
                                                making any captured frame unscannable by QR readers while
                                                the human eye averages both frames into a clean QR. */}
                                            {/* When the merchant has disabled `qr_anti_screenshot`,
                                                we render a plain static <img> instead of the
                                                temporal-multiplexed canvas. The visible <img>
                                                doubles as the qr-image testid hook in that
                                                branch; the hidden fallback below is only used
                                                when AntiScreenshotQR is active (canvas can't be
                                                queried by automation as easily). */}
                                            {data.qr_anti_screenshot === false ? (
                                                <img
                                                    src={data.qr_image}
                                                    alt="QR"
                                                    data-testid="qr-image"
                                                    className={`w-24 h-24 sm:w-44 sm:h-44 ${screenshotShield ? "opacity-0" : ""}`}
                                                    draggable={false}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                />
                                            ) : (
                                                <>
                                                    <AntiScreenshotQR
                                                        src={data.qr_image}
                                                        size={176}
                                                        shielded={screenshotShield}
                                                        alt="QR"
                                                        className="w-24 h-24 sm:w-44 sm:h-44"
                                                    />
                                                    {/* Hidden img for testability + accessibility fallback */}
                                                    <img
                                                        src={data.qr_image}
                                                        alt="QR"
                                                        data-testid="qr-image"
                                                        className="hidden"
                                                        draggable={false}
                                                    />
                                                </>
                                            )}
                                            {/* Hold-to-view overlay: covers the QR with an opaque dark panel
                                                when the merchant has enabled qr_hold_to_reveal on the link
                                                and no pointer is currently held. Pointer events on the cover
                                                reveal the QR; release/leave/cancel re-covers. */}
                                            {qrHoldMode && !qrRevealing && !screenshotShield && (
                                                <div
                                                    data-testid="qr-hold-cover"
                                                    role="presentation"
                                                    aria-label="Hold to view QR"
                                                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setQrRevealing(true); }}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 rounded-lg ring-2 ring-amber-400 px-2 text-center cursor-pointer select-none"
                                                    style={{
                                                        WebkitUserSelect: "none",
                                                        userSelect: "none",
                                                        WebkitTouchCallout: "none",
                                                        WebkitTapHighlightColor: "transparent",
                                                        touchAction: "none",
                                                    }}
                                                >
                                                    <Lock className="w-5 h-5 sm:w-7 sm:h-7 text-amber-300 mb-1" />
                                                    <div className="text-[9px] sm:text-[11px] font-bold text-amber-200 leading-tight">Hold to view QR</div>
                                                    <div className="text-[8px] sm:text-[10px] text-amber-100/80 leading-tight mt-0.5">Release to hide</div>
                                                </div>
                                            )}
                                            {qrHoldMode && qrRevealing && (
                                                <div
                                                    data-testid="qr-reveal-catcher"
                                                    onPointerUp={() => setQrRevealing(false)}
                                                    onPointerLeave={() => setQrRevealing(false)}
                                                    onPointerCancel={() => setQrRevealing(false)}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    className="absolute inset-0 rounded-lg cursor-grab"
                                                    style={{ touchAction: "none", background: "transparent" }}
                                                />
                                            )}
                                            {screenshotShield && (
                                                <div data-testid="screenshot-shield"
                                                     className="absolute inset-0 flex flex-col items-center justify-center bg-rose-50/95 rounded-lg ring-2 ring-rose-300 px-2 text-center">
                                                    <ShieldCheck className="w-5 h-5 sm:w-7 sm:h-7 text-rose-600 mb-1" />
                                                    <div className="text-[9px] sm:text-[11px] font-bold text-rose-800 leading-tight">QR hidden</div>
                                                    <div className="text-[8px] sm:text-[10px] text-rose-700 leading-tight mt-0.5">
                                                        Sharing forbidden
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                        {!qrHoldMode && (
                                            <div className="mt-1 text-[10px] sm:text-[11px] text-slate-400">Tap QR to zoom</div>
                                        )}
                                        {/* Optional QR download. Merchant-toggleable via
                                            `qr_allow_download` on the payment link. Hidden in
                                            qrHoldMode + while screenshot-shield is active so we
                                            don't accidentally serve the protected QR to a
                                            paste-bot the partner is shielding against.
                                            When `qr_download_with_caption` is ALSO on, we use a
                                            canvas to composite a "Pay ₹X to Merchant" caption
                                            below the QR before download — pure frontend, no
                                            backend cost. */}
                                        {data.qr_allow_download && !qrHoldMode && !screenshotShield && (
                                            <button
                                                type="button"
                                                data-testid="qr-download"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    // Fire telemetry; if the
                                                    // link has extend_on_share
                                                    // enabled, bump the local
                                                    // timer with the new
                                                    // expires_at returned by
                                                    // the backend.
                                                    const tele = api.post(
                                                        `/public/payment-page/${data.txn_id}/qr-shared`,
                                                        { kind: "download" },
                                                    ).catch(() => null);
                                                    downloadQr(data);
                                                    const r = await tele;
                                                    const ext = r?.data;
                                                    if (ext?.extended && ext.new_expires_at) {
                                                        setData((d) => ({ ...d,
                                                            expires_at: ext.new_expires_at,
                                                            session_extended: true }));
                                                    }
                                                }}
                                                className="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-[11px] text-blue-600 hover:text-blue-700 hover:underline underline-offset-2"
                                            >
                                                <Download className="w-3 h-3" />
                                                Download QR (PNG)
                                            </button>
                                        )}
                                    </div>
                                )}
                                {data.manual_upi_mode ? (
                                    <ManualUpiPanel
                                        upiId={snap.upi_id}
                                        amount={data.amount}
                                        txnId={data.txn_id}
                                        hideUpiId={data.hide_upi_id}
                                        hideLaunchers={data.manual_upi_hide_launchers}
                                        recopyPill={data.manual_upi_recopy_pill}
                                    />
                                ) : (
                                    <UpiIntentButtons
                                        upiIntent={data.upi_intent}
                                        snap={snap}
                                        amount={data.amount}
                                        txnId={data.txn_id}
                                        preferredApp={data.customer_preferred_app}
                                        bucketRecommendedApp={data.bucket_recommended_app}
                                        onIntentTap={onIntentTap}
                                    />
                                )}
                                {/* "Show QR" toggle for the classic intent flow. Hidden in
                                    manual UPI mode (the QR is always shown there as the
                                    primary path). Styled as a discoverable pill chip with
                                    a QR icon — text-only links were getting missed by
                                    customers paying on behalf of someone else who needed
                                    to scan from another device. */}
                                {!data.manual_upi_mode && data.qr_image && (
                                    <button
                                        type="button"
                                        data-testid="toggle-qr-visibility"
                                        onClick={() => setQrVisible((v) => !v)}
                                        aria-expanded={qrVisible}
                                        className={`mt-2 sm:mt-3 w-full flex items-center justify-center gap-2 text-[12px] sm:text-[13px] font-medium py-2 px-3 rounded-lg border transition-all
                                            ${qrVisible
                                                ? "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                                                : "bg-white border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-sm"}`}
                                    >
                                        <QrCode className="w-3.5 h-3.5" aria-hidden="true" />
                                        {qrVisible ? "Hide QR code" : "Show QR code (scan from another device)"}
                                    </button>
                                )}
                                {/* Anti-decline nudge banner — shows after the customer's first
                                    return-from-UPI-app without paying, or after we auto-rotate. */}
                                {(rotateToast || rotateBusy) && (
                                    <div data-testid="intent-fail-toast"
                                         className="mt-2 px-3 py-2 rounded-md text-[11px] sm:text-xs bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-2 animate-in fade-in slide-in-from-top-1">
                                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            {rotateBusy ? "Switching to a fresh channel…" : rotateToast}
                                        </div>
                                        {!rotateBusy && data.auto_rotate_on_fail && failCount >= 1 && (
                                            <button data-testid="rotate-now-btn" type="button"
                                                    onClick={tryRotate}
                                                    className="text-amber-900 underline font-semibold hover:text-amber-700">
                                                Rotate now
                                            </button>
                                        )}
                                    </div>
                                )}
                                {!data.manual_upi_mode && !data.hide_upi_id && snap.upi_id && (
                                    <div className={`grid grid-cols-1 gap-2 mt-2.5 sm:mt-3 transition-all duration-200 ${screenshotShield ? "blur-md select-none pointer-events-none" : ""}`}>
                                        <FieldCopy k="UPI ID" v={snap.upi_id} ck="upi" copied={copied} onCopy={copy} />
                                    </div>
                                )}
                                {/* Screenshot warning - desktop only (mobile is space-constrained) */}
                                <div className="hidden sm:flex mt-3 text-[10px] text-slate-500 leading-relaxed bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 items-start gap-1.5">
                                    <ShieldCheck className="w-3 h-3 text-amber-700 mt-0.5 flex-shrink-0" />
                                    <span>
                                        {data.hide_upi_id
                                            ? "For your safety, only the QR & UPI app shortcuts are available. Sharing or screenshotting is traceable and may delay verification."
                                            : "This QR & UPI ID are uniquely tagged to your session. Sharing or screenshotting is traceable and may delay verification."}
                                    </span>
                                </div>
                            </div>
                        )}
                        {data.method === "bank" && snap.account_number && (
                            <div className="p-3 sm:p-5 pay-protected">
                                <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 sm:mb-3 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Bank transfer (NEFT/IMPS)</div>
                                <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                                    <FieldCopy k="A/c holder" v={snap.account_holder} ck="ah" copied={copied} onCopy={copy} />
                                    <FieldCopy k="A/c number" v={snap.account_number} ck="an" copied={copied} onCopy={copy} mono />
                                    <FieldCopy k="IFSC" v={snap.ifsc} ck="ifsc" copied={copied} onCopy={copy} mono />
                                    <FieldCopy k="Bank" v={snap.bank_name} ck="bk" copied={copied} onCopy={copy} />
                                </div>
                                <div className="hidden sm:flex mt-3 text-[10px] text-slate-500 leading-relaxed bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 items-start gap-1.5">
                                    <ShieldCheck className="w-3 h-3 text-amber-700 mt-0.5 flex-shrink-0" />
                                    <span>
                                        These bank details are uniquely tagged to your session. Sharing or
                                        screenshotting is traceable and may delay verification.
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="p-3 sm:p-5 border-t border-slate-200 bg-slate-50/50">
                            <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 sm:mb-2.5">
                                After paying — submit UTR
                            </div>
                            <form onSubmit={submit} className="space-y-2 sm:space-y-3">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="label !mb-0">
                                            UTR / Reference number
                                            {screenshot && (
                                                <span className="ml-1.5 text-[10px] font-normal text-slate-500">(optional if screenshot is attached)</span>
                                            )}
                                        </label>
                                        {autoFilled.utr && (
                                            <span data-testid="utr-autofilled-badge" className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                                                <Sparkles className="w-3 h-3" /> Auto-filled
                                            </span>
                                        )}
                                    </div>
                                    {/* UTR is only `required` when no screenshot is attached. Once
                                        the customer adds a payment screenshot, the field becomes
                                        optional — they can submit on the screenshot alone and the
                                        merchant approves manually. minLength is also gated so
                                        empty-string + screenshot doesn't trip HTML5 validation. */}
                                    <input data-testid="public-utr"
                                        required={!screenshot}
                                        minLength={screenshot ? 0 : 6}
                                        inputMode="numeric"
                                        value={utr}
                                        onChange={(e) => { setUtr(e.target.value); setAutoFilled((a) => ({ ...a, utr: false })); }}
                                        className={`input font-mono ${autoFilled.utr ? "ring-2 ring-emerald-300 border-emerald-300" : ""}`}
                                        placeholder={screenshot ? "Optional — already have a screenshot" : "12-digit reference"} />
                                </div>
                                <div>
                                    <label className="label hidden sm:block">Upload screenshot (optional · auto-fills UTR)</label>
                                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="public-screenshot" className="hidden" />
                                    <button type="button" onClick={() => fileRef.current?.click()} className="btn-secondary w-full py-1.5 sm:py-2.5 rounded-md text-xs sm:text-sm flex items-center justify-center gap-2">
                                        {ocrBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading screenshot…</> :
                                          screenshot ? <><ImageIcon className="w-4 h-4 text-emerald-600" /> Replace screenshot</> :
                                          <><Upload className="w-4 h-4" /> Upload screenshot <span className="hidden sm:inline opacity-60">(auto-fills UTR)</span></>}
                                    </button>
                                    {ocrResult && ocrResult.kind === "success" && !ocrBusy && (
                                        <div data-testid="ocr-feedback" className="mt-2 text-[11px] rounded-md p-2 flex items-start gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800">
                                            <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <span className="font-medium">
                                                    {ocrResult.provenance === "client" ? "Quick OCR" : "Smart OCR"}:
                                                </span> UTR auto-filled.
                                                {ocrResult.provenance === "client" && (
                                                    <span className="opacity-70"> Read locally — free.</span>
                                                )}
                                                {ocrResult.app && ocrResult.app !== "Other" && (
                                                    <span className="opacity-70"> Detected {ocrResult.app}.</span>
                                                )}{" "}
                                                Please verify before submitting.
                                            </div>
                                        </div>
                                    )}
                                    {/* Soft-fail banner: OCR couldn't read the UTR but the
                                        screenshot is still attached. Customer can either type
                                        the UTR OR just hit Submit — backend accepts both. */}
                                    {ocrResult && ocrResult.kind === "soft_fail" && !ocrBusy && screenshot && (
                                        <div data-testid="ocr-softfail-banner" className="mt-2 text-[11px] rounded-md p-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 text-amber-900">
                                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <span className="font-medium">Couldn&apos;t read UTR.</span>{" "}
                                                {ocrResult.reason || "You can still submit — the merchant will verify your screenshot manually."}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {expired && (
                                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                                        Session timer ran out, but you can still submit your UTR if you&apos;ve already paid. The merchant will verify it manually.
                                    </div>
                                )}
                                {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2.5">{err}</div>}
                                {/* Submit disabled until at least one of (UTR ≥ 6 chars, screenshot)
                                    is supplied — mirrors the backend's `_require_utr_or_screenshot`
                                    validator so the gate is the same on both sides. */}
                                <button data-testid="submit-utr-btn"
                                        disabled={busy || (utr.trim().length < 6 && !screenshot)}
                                        type="submit"
                                        className="btn-emerald w-full py-2 sm:py-2.5 rounded-md text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {screenshot && utr.trim().length < 6 ? "Submit screenshot for review" : "Submit UTR"}
                                </button>
                            </form>
                        </div>
                    </div>
                    </main>

                    {/* ── Right panel: trust signals & help (desktop ≥lg) */}
                    <aside className="hidden lg:flex lg:col-span-3 lg:sticky lg:top-6 flex-col gap-3 self-start">
                        <div className="card p-4">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Secure & Encrypted
                            </div>
                            <ul className="text-[11px] text-slate-600 space-y-1.5">
                                <li>• 256-bit TLS connection</li>
                                <li>• Your details never stored</li>
                                <li>• Bank-grade UPI rails</li>
                            </ul>
                        </div>
                        <div className="card p-4 bg-blue-50/50 border-blue-100">
                            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-2 flex items-center gap-1.5">
                                <Smartphone className="w-3.5 h-3.5" /> How to pay
                            </div>
                            <ol className="text-[11px] text-slate-700 space-y-1.5 list-decimal pl-4">
                                {data.method === "upi" ? (
                                    <>
                                        <li>Tap &quot;Pay with UPI app&quot; or scan the QR with any UPI app.</li>
                                        <li>Authorise the payment in your UPI app.</li>
                                        <li>Copy the 12-digit UTR / Ref number from the success screen.</li>
                                        <li>Paste it here and click <span className="font-semibold">Submit UTR</span>.</li>
                                    </>
                                ) : (
                                    <>
                                        <li>Open your bank app, add the payee details shown above.</li>
                                        <li>Transfer the exact amount.</li>
                                        <li>Copy the UTR / Reference number from your bank statement.</li>
                                        <li>Paste it here and click <span className="font-semibold">Submit UTR</span>.</li>
                                    </>
                                )}
                            </ol>
                        </div>
                        <div className="card p-4 bg-slate-50 border-slate-200">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Need help?</div>
                            <p className="text-[11px] text-slate-600">Contact <span className="font-medium">{data.partner_name}</span> with your transaction id below if you&apos;re stuck.</p>
                            <div className="font-mono text-[10px] text-slate-500 mt-1.5 break-all">{data.txn_id}</div>
                        </div>
                    </aside>
                </div>
            </div>
            {qrZoom && data?.qr_image && (
                <QrZoomModal
                    data={data}
                    screenshotShield={screenshotShield}
                    qrHoldMode={qrHoldMode}
                    qrRevealing={qrRevealing}
                    setQrRevealing={setQrRevealing}
                    onClose={() => setQrZoom(false)}
                    setData={setData}
                />
            )}
            {/* Note: the old "Couldn't read your screenshot" blocking modal was
                removed in favour of an inline soft-fail banner under the upload
                button (see ocr-softfail-banner). The submit button is now gated
                on (utr ≥ 6 chars OR screenshot attached) — never both required. */}
        </div>
    );
}
